import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { AlertService } from '@context/frontend/alert';
import { FIRESTORE } from '@context/frontend/common';
import { ExternalViewContextService } from '@context/frontend/user';
import { Content } from '@context/shared/types/common';
import { doc, DocumentReference, getDoc } from 'firebase/firestore';

export type ExternalContentResolved = {
	data: Content;
	ref: DocumentReference<Content>;
};

/**
 * Resolves the content for an external viewer route.
 *
 * The root route (no `:id`) falls back to the shared content id captured by the
 * {@link ExternalViewAuthGuard}. Descendant routes resolve by `:id`. Anything that
 * cannot be read (deleted, missing, or outside the shared subtree - the latter is
 * denied by the Firestore rules) sends the viewer back to the share root, or to the
 * landing page when even the root is unavailable.
 */
export const externalContentResolver: ResolveFn<
	ExternalContentResolved | null
> = async (route: ActivatedRouteSnapshot) => {
	const context = inject(ExternalViewContextService);
	const firestore = inject(FIRESTORE);
	const router = inject(Router);
	const alert = inject(AlertService);

	const ctx = context.data();
	if (!ctx) {
		await router.navigate(['/']);
		return null;
	}

	const id = route.paramMap.get('id') ?? ctx.contentId;

	const toRoot = async (): Promise<null> => {
		// If even the shared root cannot be loaded, bail to the landing page.
		if (id === ctx.contentId) {
			await router.navigate(['/']);
			alert.open('external-content-not-found').subscribe();
			return null;
		}
		// Otherwise send the viewer back to the shared root content.
		await router.navigate([
			'/content',
			ctx.externalId,
			ctx.type,
			ctx.contentId,
		]);
		return null;
	};

	const ref = doc(
		firestore,
		'organizations',
		ctx.organizationId,
		'content',
		id,
	) as DocumentReference<Content>;

	try {
		const snapshot = await getDoc(ref);
		const data = snapshot.data() as Content | undefined;
		if (!snapshot.exists() || !data || data.deletedAt) {
			return await toRoot();
		}

		return { data, ref };
	} catch (error) {
		console.error('External content resolve failed', error);
		return await toRoot();
	}
};
